import { isValidElement } from 'react';
import { Text } from '@figma/fpl-components';
import { borderRadius, color, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { Link } from 'react-router-dom';
import { generatorPath } from '@/components/Generate/generatorPath';
import { resolveMenuItemIcon } from '@/components/Recipe/FlowComponents/LeftPanelSections/NodeIconMap';
import { CarouselArrows } from './CarouselArrows';
import { cardMarker, rowMarker, toolkitLayout } from './toolkit.stylex';
import { LOOP_COPIES, REAL_COPY, useCarousel } from './useCarousel';
import type { ModelCatalogCard, ModelMaker } from '@/types/toolkit.types';

/**
 * Each maker's icon name in the lookup the generator's model menu uses, so a card shows the same icon as
 * the menu. It must match the `icon` of the maker's models in `generate.api`. The generator panel shows
 * its line of logos in this order, as in the design.
 */
const MAKER_ICONS: Record<ModelMaker, string> = {
  google: 'gemini',
  openai: 'openai',
  flux: 'black_forest',
  ideogram: 'ideogram',
  bytedance: 'bytedance',
  kling: 'kling',
};

export const MAKERS_IN_ORDER = Object.keys(MAKER_ICONS) as ModelMaker[];

export function MakerLogo({ maker }: { maker: ModelMaker }) {
  const icon = resolveMenuItemIcon({ icon: MAKER_ICONS[maker] });
  return isValidElement(icon) ? icon : null;
}

const styles = stylex.create({
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer3,
    minWidth: 0,
  },
  headings: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacer.spacer1,
  },
  text: {
    margin: 0,
  },
  body: {
    position: 'relative',
  },
  viewport: {
    outline: {
      default: 'none',
      ':focus-visible': '1px solid currentColor',
    },
    overflowX: 'auto',
    overscrollBehaviorX: 'contain',
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  // Two lines, filled one column at a time. A column is never wider than the design's 355px. Below that,
  // three columns share the content, then two and one when the content is as narrow as for tool cards
  // (`cardSize` in toolkit.stylex.ts).
  track: {
    display: 'grid',
    gap: toolkitLayout.cardGap,
    gridAutoColumns: {
      default: 'min(355px, calc((100cqw - 32px) / 3))',
      '@container (min-width: 368px) and (max-width: 919px)': 'min(355px, calc((100cqw - 16px) / 2))',
      '@container (max-width: 367px)': 'min(355px, 100cqw)',
    },
    gridAutoFlow: 'column',
    gridTemplateRows: 'repeat(2, auto)',
  },
  card: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderRadius: borderRadius.large,
    color: 'inherit',
    display: 'flex',
    gap: spacer.spacer2,
    minWidth: 0,
    padding: spacer.spacer2,
    paddingRight: spacer.spacer3,
    scrollSnapAlign: 'start',
    textDecoration: 'none',
  },
  link: {
    backgroundColor: {
      default: 'transparent',
      ':hover': color.bgHover,
      ':focus-visible': color.bgHover,
    },
    outline: {
      default: 'none',
      ':focus-visible': `2px solid ${color.borderSelected}`,
    },
    transition: {
      default: 'background-color 0.2s ease-out',
      '@media (prefers-reduced-motion: reduce)': 'none',
    },
  },
  logo: {
    alignItems: 'center',
    backgroundColor: {
      default: 'transparent',
      [stylex.when.ancestor(':hover', cardMarker)]: color.bgSecondary,
      [stylex.when.ancestor(':focus-visible', cardMarker)]: color.bgSecondary,
    },
    borderColor: color.border,
    borderRadius: borderRadius.medium,
    borderStyle: 'solid',
    borderWidth: '1px',
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    height: '56px',
    justifyContent: 'center',
    transition: {
      default: 'background-color 0.2s ease-out',
      '@media (prefers-reduced-motion: reduce)': 'none',
    },
    width: '56px',
  },
  words: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  twoLines: {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    display: '-webkit-box',
    overflow: 'hidden',
  },
});

function ModelCard({ card, isCopy }: { card: ModelCatalogCard; isCopy: boolean }) {
  return (
    // In-app navigation, so the generator reuses the model list the Toolkit loaded for it.
    <Link
      {...stylex.props(styles.card, styles.link, cardMarker)}
      to={generatorPath(card.id)}
      aria-hidden={isCopy || undefined}
      tabIndex={isCopy ? -1 : undefined}
    >
      <span {...stylex.props(styles.logo)} aria-hidden>
        <MakerLogo maker={card.maker} />
      </span>
      <span {...stylex.props(styles.words)}>
        <Text.span variant="body-medium-strong" color="default" truncate>
          {card.name}
        </Text.span>
        <Text.span {...stylex.props(styles.twoLines)} variant="body-medium" color="secondary">
          {card.description}
        </Text.span>
      </span>
    </Link>
  );
}

interface ModelRowProps {
  title: string;
  subtitle: string;
  cards: ModelCatalogCard[];
}

/**
 * The row of model cards. Two lines of small cards, filled one column at a time, so the list gives them
 * in column order. It is an endless carousel when the columns do not all fit, and has no "Show all".
 */
export function ModelRow({ title, subtitle, cards }: ModelRowProps) {
  // The row loops by column. Each copy is padded to whole columns, so a copy never splits a pair.
  const columnCount = Math.ceil(cards.length / 2);
  const slots = Array.from({ length: columnCount * 2 }, (_, index) => cards[index] ?? null);
  const { viewportRef, isLooping, move, onKeyDown } = useCarousel(columnCount);
  const copies = isLooping ? LOOP_COPIES : [REAL_COPY];

  return (
    <section {...stylex.props(styles.row)}>
      <div {...stylex.props(styles.headings)}>
        <Text.h2 {...stylex.props(styles.text)} variant="heading-small" color="default">
          {title}
        </Text.h2>
        <Text.p {...stylex.props(styles.text)} variant="body-medium" color="secondary">
          {subtitle}
        </Text.p>
      </div>
      <div {...stylex.props(styles.body, rowMarker)}>
        <div
          ref={viewportRef}
          {...stylex.props(styles.viewport)}
          role="region"
          aria-label={title}
          tabIndex={isLooping ? 0 : undefined}
          onKeyDown={isLooping ? onKeyDown : undefined}
        >
          <div {...stylex.props(styles.track)}>
            {copies.flatMap((copy) =>
              slots.map((card, index) =>
                card ? (
                  <ModelCard key={`${copy}:${card.id}`} card={card} isCopy={copy !== REAL_COPY} />
                ) : (
                  <span key={`${copy}:empty-${index}`} aria-hidden />
                ),
              ),
            )}
          </div>
        </div>
        {isLooping ? <CarouselArrows rowTitle={title} onScroll={move} /> : null}
      </div>
    </section>
  );
}
